import masks from './tw-masks.json';
import { buildTerrain, type CatapultDef, type LevelDef, type LevelStyle, type Material } from './levels';
import { ensureCrossing } from './mapgen';
import { clamp } from './rng';

/**
 * War maps: arenas laid out after the stages of the original Territory War games (the project
 * owner supplied the three games as reference). `tw-masks.json` holds coarse silhouettes of the
 * ground only, extracted by scripts/import-tw-maps.cjs: no art, no sounds, no code. Everything a
 * player sees (materials, skies, outlines) is this game's own. Each map keeps the shape of the
 * stage it came from, gets a theme, a name of its own, a way across (bridging.ts) and moles
 * placed by the same rules as the generated maps.
 */

interface MaskData {
  src: string;
  w: number;
  h: number;
  cell: number;
  rle: string;
  /** The original stage's spawn markers, when it had them (Territory War 3). */
  left?: { x: number; y: number }[];
  right?: { x: number; y: number }[];
}

const MASKS = masks as unknown as MaskData[];

interface Theme {
  material: Material;
  style: LevelStyle;
}

const THEMES = {
  meadow: {
    material: 'grass',
    style: { sky: ['#4aa3e8', '#dff3ff'], far: '#8fcf9a', near: '#5aa86a', water: 'transparent', deep: '#10281a', pit: '#10281a', horizon: 'islands', art: 'meadow' },
  },
  desert: {
    material: 'rock',
    style: { sky: ['#c2663a', '#f3c58a'], far: '#9c5a3c', near: '#6e3f2b', water: 'transparent', deep: '#1a0d08', pit: '#1a0d08', horizon: 'mesas', art: 'desert' },
  },
  dusk: {
    material: 'grass',
    style: { sky: ['#2b1f4e', '#d8798a'], far: '#4a3a6b', near: '#2f2446', water: 'transparent', deep: '#0b0912', pit: '#0b0912', horizon: 'city', art: 'city' },
  },
  dunes: {
    material: 'rock',
    style: { sky: ['#6b7b8c', '#d8cfb8'], far: '#8a7b63', near: '#5f5440', water: 'transparent', deep: '#140f0a', pit: '#140f0a', horizon: 'dunes', art: 'dunes' },
  },
  city: {
    material: 'stone',
    style: { sky: ['#4a90c8', '#dbeaf5'], far: '#7e95ad', near: '#56687c', water: 'transparent', deep: '#10151c', pit: '#10151c', horizon: 'city', art: 'cityday' },
  },
  storm: {
    material: 'stone',
    style: { sky: ['#3b4a63', '#d9c3a0'], far: '#7a8aa0', near: '#55657c', water: 'transparent', deep: '#15232e', pit: '#15232e', horizon: 'storm', art: 'storm' },
  },
  frost: {
    material: 'regolith',
    style: { sky: ['#7fa8d8', '#eef6ff'], far: '#b9cde6', near: '#8fa6c4', water: 'transparent', deep: '#16202e', pit: '#16202e', horizon: 'islands', art: 'frost' },
  },
} satisfies Record<string, Theme>;

export type WarTheme = keyof typeof THEMES;

export interface WarMapDef {
  /** The map id players pick and the lobby sends: `war:` plus a short name. */
  id: string;
  /** Which silhouette in tw-masks.json this map is laid out after. */
  src: string;
  name: string;
  blurb: string;
  theme: WarTheme;
  /** Sky above the tallest ground, px: room for lobbed shots. */
  sky: number;
  /** Catapults on both sides: the chasm maps. */
  catapults?: boolean;
  /** Longer maps give more walking. */
  stamina?: number;
  /** The stage is blown up by this much (small stages are too tight for two crews). */
  scale?: number;
}

export const WAR_MAPS: readonly WarMapDef[] = [
  // Territory War: the first game's six stages (long, low strips: chasms, steps and floating bars).
  { id: 'war:tiers', src: 'TW1 MAP1', name: 'Tiers', blurb: 'A stepped hill between two ditches. Whoever holds the top shells the other side.', theme: 'meadow', sky: 760 },
  { id: 'war:watch', src: 'TW1 MAP2', name: 'Watchtower', blurb: 'A tall tower on the left, a broken staircase of ledges on the right.', theme: 'desert', sky: 780 },
  { id: 'war:crossing', src: 'TW1 MAP3', name: 'Crossing', blurb: 'Two ledges, a bottomless gap and a few floating bars. The catapults are free.', theme: 'dusk', sky: 840, catapults: true },
  { id: 'war:pyramid', src: 'TW1 MAP4', name: 'Pyramid', blurb: 'A stepped mountain in the middle of a flat field. Climb it or shell over it.', theme: 'dunes', sky: 640 },
  { id: 'war:hollow', src: 'TW1 MAP5', name: 'Hollow', blurb: 'A bowl in the middle with ledges up both slopes and plank platforms overhead.', theme: 'meadow', sky: 700 },
  { id: 'war:gate', src: 'TW1 MAP6', name: 'Gatehouse', blurb: 'Two walls with a gap between them and three crates floating above it. Catapults on both sides.', theme: 'city', sky: 820, catapults: true },
  // Territory War 2: wider stages with towers at the ends and pillars between.
  { id: 'war:twin', src: 'TW2 bitmap 1907', name: 'Twin Towers', blurb: 'A tower at each end, pillars in the middle, a long field between.', theme: 'city', sky: 620 },
  { id: 'war:pillars', src: 'TW2 bitmap 1900', name: 'Pillars', blurb: 'Stone pillars along the ridge give cover and a view.', theme: 'desert', sky: 640, scale: 1.15 },
  { id: 'war:bunkers', src: 'TW2 bitmap 1859', name: 'Bunkers', blurb: 'Dug-in banks at each end and a flat killing ground between.', theme: 'dunes', sky: 640 },
  { id: 'war:ridge', src: 'TW2 bitmap 1870', name: 'Ridge', blurb: 'One long ridge that sags in the middle.', theme: 'meadow', sky: 620 },
  { id: 'war:steps', src: 'TW2 bitmap 798', name: 'Steps', blurb: 'A staircase up the left side and a high shelf on the right.', theme: 'storm', sky: 640, scale: 1.4 },
  { id: 'war:yard', src: 'TW2 bitmap 830', name: 'Yard', blurb: 'A walled yard with a raised block in the middle.', theme: 'dusk', sky: 640, scale: 1.4 },
  { id: 'war:keep', src: 'TW2 bitmap 1921', name: 'Keep', blurb: 'A tall keep on the left and open ground to the right.', theme: 'city', sky: 560, scale: 1.8 },
  // Territory War 3: tall, hand-drawn stages.
  { id: 'war:summit', src: 'TW3 map_0', name: 'Summit', blurb: 'A single huge peak with a ledge halfway up. Bring a grapple.', theme: 'frost', sky: 160, stamina: 1.5 },
  { id: 'war:causeway', src: 'TW3 map_1', name: 'Causeway', blurb: 'A long flat road with a rocky island in the middle.', theme: 'meadow', sky: 620 },
  { id: 'war:hills', src: 'TW3 map_2', name: 'Twin Hills', blurb: 'Round hills on both sides, a valley and a floating shelf between them.', theme: 'meadow', sky: 160, stamina: 1.3 },
  { id: 'war:highlands', src: 'TW3 map_3', name: 'Highlands', blurb: 'Plateaus, shelves and a long drop in the middle.', theme: 'desert', sky: 400, stamina: 1.3, catapults: true },
  { id: 'war:plateaus', src: 'TW3 map_4', name: 'Plateaus', blurb: 'Rolling ground with a pair of high plateaus.', theme: 'dunes', sky: 460, stamina: 1.3 },
  { id: 'war:citadel', src: 'TW3 map_6', name: 'Citadel', blurb: 'Walled courtyards on both sides and a deep moat between.', theme: 'storm', sky: 700, catapults: true },
  { id: 'war:badlands', src: 'TW3 map_7', name: 'Badlands', blurb: 'Broken mesas and gullies, long walks and lots of cover.', theme: 'desert', sky: 700, stamina: 1.2 },
];

const BY_ID = new Map(WAR_MAPS.map((m) => [m.id, m]));

export function isWarMap(id: string): boolean {
  return BY_ID.has(id);
}

export function warMapDef(id: string): WarMapDef | undefined {
  return BY_ID.get(id);
}

/** Where a side's catapult stands: a flat spot a little toward the enemy from the side's innermost mole. */
function catapultFor(level: LevelDef, side: 0 | 1): CatapultDef | null {
  const spawn = level.spawns[side]?.[0];
  if (!spawn) return null;
  const dir: 1 | -1 = side === 0 ? 1 : -1;
  const t = buildTerrain(level);
  const ground = (x: number): number => t.surfaceY(x, Math.max(0, spawn[1] - 140)) - 1;
  // Flat for 50 px either side (the frame and its wheels), so nothing hangs over a drop.
  const flat = (x: number, y: number): boolean => [-50, -25, 25, 50].every((d) => Math.abs(ground(x + d) - y) <= 10);
  for (const dx of [130, 170, 90, 210, 250, 60, 40]) {
    const x = Math.round(spawn[0] + dir * dx);
    const y = ground(x);
    if (Math.abs(y - spawn[1]) > 26 || !flat(x, y)) continue;
    return { x, y, dir };
  }
  // No flat run near the crew: stand it where the innermost mole stands, which is flat by construction.
  return { x: Math.round(spawn[0]), y: spawn[1], dir };
}

const cache = new Map<string, LevelDef>();

/** The level for a war map id, built once per session (and identically on every client). */
export function warLevel(id: string): LevelDef | null {
  const def = BY_ID.get(id);
  if (!def) return null;
  const hit = cache.get(id);
  if (hit) return hit;
  const mask = MASKS.find((m) => m.src === def.src);
  if (!mask) return null;
  const theme = THEMES[def.theme];
  const k = def.scale ?? 1;
  const width = Math.round(mask.w * k);
  const level: LevelDef = {
    id: def.id, name: def.name, blurb: def.blurb, width, height: def.sky + Math.round(mask.h * k), waterY: null,
    shapes: [{ kind: 'mask', x: 0, y: def.sky, cell: mask.cell * k, w: width, h: Math.round(mask.h * k), rle: mask.rle, m: theme.material }],
    boats: [], spawns: [[], [], []], floats: [[], [], []],
    gravity: 1, stamina: def.stamina ?? clamp(width / 1900, 1, 1.4), jump: 1, style: theme.style,
  };
  const at = (list: { x: number; y: number }[] | undefined) => list?.map((p) => ({ x: p.x * k, y: p.y * k + def.sky })) ?? [];
  ensureCrossing(level, mask.left && mask.right ? { platform: true, markers: { left: at(mask.left), right: at(mask.right) } } : { platform: true });
  if (def.catapults) {
    const a = catapultFor(level, 0);
    const b = catapultFor(level, 1);
    level.catapults = [a, b].filter((c): c is CatapultDef => !!c);
  }
  cache.set(id, level);
  return level;
}

/** Tags the map picker filters by. */
export function warTags(def: WarMapDef): string[] {
  const tags = ['war', `tw${def.src.slice(2, 3)}`, 'land'];
  if (def.catapults) tags.push('catapults');
  return tags;
}
